import { useEffect, useMemo, useRef, useState } from 'react';

import { transactionLineItems } from '../../util/api';
import {
  defaultDeliveryOption,
  deliveryOptionsFor,
  initialQuantity,
  listingOrderSummary,
  orderDataForDelivery,
  quantityBounds,
  summaryFromLineItems,
} from '../../util/listingOrderSummary';
import { isVatRegisteredSeller } from '../../util/orderVatCore';

/* Stepper clicks come in bursts; price the resting value, not every step. */
const CONFIRM_DELAY_MS = 250;

/**
 * The buyer's order as the listing page builds it (P25009-482 BR-2 / BR-5 /
 * BR-6): quantity, delivery choice, and what that costs.
 *
 * One owner for the state, because three surfaces show it — the desktop buy
 * box, the phone buy card (the same component) and the sticky bar — and they
 * must never disagree about the quantity or the total.
 *
 * The total is printed at once from listingOrderSummary, then confirmed with
 * the server's own line items for the same quantity and delivery. The server's
 * number wins: it is the function checkout charges with, so what the buyer
 * sees here is what checkout will show (BR-6). A late answer for a quantity the
 * buyer has already left is dropped.
 *
 * @param {Object} params
 * @param {Object} params.listing the current listing (with author + currentStock)
 * @param {boolean} params.canOrder false for own/inquiry/closed listings — no
 *   server pricing is asked for then
 * @param {boolean} params.isOwnListing passed through to the pricing endpoint
 */
const useBuyBoxOrder = ({ listing, canOrder, isOwnListing }) => {
  const listingId = listing?.id?.uuid;
  const publicData = listing?.attributes?.publicData || {};
  const stock = listing?.currentStock?.attributes?.quantity;
  const trackedStock = typeof stock === 'number' ? stock : null;

  const bounds = quantityBounds(publicData, trackedStock);
  const options = deliveryOptionsFor({ publicData, author: listing?.author });

  const [quantity, setQuantityState] = useState(() => initialQuantity(publicData, trackedStock));
  const [optionKey, setOptionKey] = useState(() => defaultDeliveryOption(options)?.key || null);
  const [confirmed, setConfirmed] = useState(null);

  /* A different listing starts over: its own default quantity and delivery. */
  const startedFor = useRef(listingId);
  useEffect(() => {
    if (startedFor.current === listingId) return;
    startedFor.current = listingId;
    setQuantityState(initialQuantity(publicData, trackedStock));
    setOptionKey(defaultDeliveryOption(options)?.key || null);
    setConfirmed(null);
  }, [listingId]);

  // Stock can arrive after the first render; keep the choice inside it.
  useEffect(() => {
    if (quantity > bounds.max) setQuantityState(bounds.max);
  }, [bounds.max]);

  const option = options.find(o => o.key === optionKey) || defaultDeliveryOption(options);

  const setQuantity = next => {
    const n = Number.parseInt(next, 10);
    if (!Number.isFinite(n)) return;
    setQuantityState(Math.max(bounds.min, Math.min(bounds.max, n)));
  };

  /* BR-9 (P25009-484) — the delivery chosen here opens checkout already
   * selected. Montering on new complete wheels needs no id: the estimate
   * prices it and the server adds it by itself (P25009-484 BR-5). */
  const orderData = useMemo(() => ({ quantity, ...orderDataForDelivery(option) }), [
    quantity,
    option?.key,
  ]);

  const estimate = listingOrderSummary({ listing, quantity, option });
  const requestKey = `${listingId}|${quantity}|${option?.key || 'none'}`;

  useEffect(() => {
    if (!canOrder || !listingId || !estimate) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      transactionLineItems({
        listingId: listing.id,
        isOwnListing,
        orderData: {
          stockReservationQuantity: quantity,
          ...(orderData.deliveryMethod ? { deliveryMethod: orderData.deliveryMethod } : {}),
        },
      })
        .then(response => {
          if (cancelled) return;
          const fromServer = summaryFromLineItems(response?.data, {
            sellerChargesVat: isVatRegisteredSeller(listing?.author),
          });
          if (fromServer) setConfirmed({ key: requestKey, ...fromServer });
        })
        .catch(() => {
          // The estimate stays on screen; checkout prices again anyway.
        });
    }, CONFIRM_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [requestKey, canOrder]);

  const serverAnswer = confirmed?.key === requestKey ? confirmed : null;
  const summary = estimate
    ? {
        ...estimate,
        ...(serverAnswer
          ? {
              totalSubunits: serverAnswer.totalSubunits,
              vatSubunits: serverAnswer.vatSubunits,
              deliverySubunits: serverAnswer.deliverySubunits,
              servicesSubunits: serverAnswer.servicesSubunits,
              miljoavgiftSubunits: serverAnswer.miljoavgiftSubunits,
            }
          : {}),
        confirmed: !!serverAnswer,
      }
    : null;

  return {
    quantity,
    setQuantity,
    bounds,
    options,
    option,
    setOptionKey,
    orderData,
    summary,
  };
};

export default useBuyBoxOrder;
